import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { Store } from '@ngrx/store';
import { Observable, map, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipGeneratorContextType, IBetslip } from '../../../bet-generation/models';
import { IRewardToken, RewardTokenContext } from '../../../reward-tokens/reward-tokens.model';
import { selectEligibleTokenFactory } from '../../../reward-tokens/selectors';
import { RewardTokensBetslipSummaryService } from '../../../reward-tokens/services/reward-tokens-betslip-summary.service';
import { IBetslipSummary } from '../../models';
import { BetslipSummaryService } from '../../services/betslip-summary.service';

@Component({
    selector: 'bs-digital-token-summary-container',
    templateUrl: './token-summary-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TokenSummaryContainerComponent implements OnChanges {
    @Input() tokenContext: RewardTokenContext;

    vm$: Observable<{ token?: IRewardToken; message?: string }>;

    constructor(
        private summaryService: BetslipSummaryService,
        private tokensSummaryService: RewardTokensBetslipSummaryService,
        private store: Store<IBetslipRootState>,
    ) {}

    ngOnChanges(changes: ISimpleChanges<TokenSummaryContainerComponent>) {
        const previousContext = changes.tokenContext?.previousValue;
        const currentContext = changes.tokenContext?.currentValue;

        if (
            !!previousContext &&
            !!currentContext &&
            previousContext.betslipType === currentContext.betslipType &&
            previousContext.pickId?.toString() === currentContext.pickId?.toString()
        ) {
            return;
        }

        const token$ = this.store.select(selectEligibleTokenFactory(this.tokenContext));

        const summary$ = this.getSummaryForContext$();

        this.vm$ = summary$.pipe(
            withLatestFrom(token$),
            map(([[_, summary], token]) => {
                if (!token) {
                    return {};
                }

                const slipSummary = summary.slipSummaries.find(({ slip }) => slip.rewardToken?.userTokenId === token.userTokenId);

                const message = this.tokensSummaryService.getTokenSummary(token, slipSummary) ?? undefined;

                return { token, message };
            }),
        );
    }

    private getSummaryForContext$(): Observable<[IBetslip, IBetslipSummary]> {
        if (this.tokenContext.betslipType === BetslipType.Single) {
            return this.summaryService.getSingleBetSummaryForPick$(BetslipGeneratorContextType.Betslip, this.tokenContext.pickId!);
        }

        if (this.tokenContext.betslipType === BetslipType.BetBuilder) {
            return this.summaryService.getBetBuilderBetSummaryForPick$(BetslipGeneratorContextType.Betslip, this.tokenContext.pickId!);
        }

        return this.summaryService.getComboBetSummary$(BetslipGeneratorContextType.Betslip);
    }
}
